"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.phoneScreenPeopleSchema = exports.PHONE_SCREEN_BULK_MAX = exports.undoPhoneScreenPassSchema = exports.phoneScreenResultSchema = exports.removePhoneScreenRoundSchema = exports.renamePhoneScreenRoundSchema = exports.addPhoneScreenRoundSchema = exports.switchPhoneScreenSchema = exports.PHONE_SCREEN_LABEL = exports.PHONE_SCREEN_MAX_ROUNDS = exports.PHONE_SCREEN_TOPIC_MAX = exports.PHONE_SCREEN_OUT_REASON_CODE = void 0;
exports.phoneScreenRoundName = phoneScreenRoundName;
exports.phoneScreenOutNote = phoneScreenOutNote;
exports.isPastShortlist = isPastShortlist;
exports.isInPhoneScreen = isInPhoneScreen;
exports.waitingRoundOf = waitingRoundOf;
exports.placeInRound = placeInRound;
exports.phoneScreenRefusal = phoneScreenRefusal;
exports.undoPassRefusal = undoPassRefusal;
exports.nextRoundNumber = nextRoundNumber;
exports.roundRemovalRefusal = roundRemovalRefusal;
exports.switchOffRefusal = switchOffRefusal;
const zod_1 = require("zod");
const schemas_1 = require("./schemas");
// ADR 0079 — the optional phone Screening step (workflow review D2, DECISIONS-LOG 2026-11-01 Q6, Q6b).
// Off by default, switched on per job from the Shortlist; when on it sits between ⑤ Shortlist and
// ⑥ Contact. HR adds "Screening 1" with what it checks; everyone in the step is in Screening 1 and gets
// Pass or Out. "Screening 2" holds only Screening 1's passes, and so on. At any round HR may tick people
// and send them to Contact. Out is a rejection, SCR-PHONE, whose note names the screening. Screen PHS-01.
/** §L19: what Out records. Its note names the screening ("Screening 1: Can they move for this job?"). */
exports.PHONE_SCREEN_OUT_REASON_CODE = 'SCR-PHONE';
/** What a round checks, in HR's words, at most this long. */
exports.PHONE_SCREEN_TOPIC_MAX = 200;
/** Enough for any phone round anyone runs; it keeps the round tabs on one line on a laptop. */
exports.PHONE_SCREEN_MAX_ROUNDS = 10;
/** The stage bar's and the screen's name for the step (the owner's own word, Q6). */
exports.PHONE_SCREEN_LABEL = 'Screening';
function phoneScreenRoundName(number) {
    return `${exports.PHONE_SCREEN_LABEL} ${number}`;
}
/** The rejection note an Out writes: which screening, and what it checked. */
function phoneScreenOutNote(number, topic) {
    return `${phoneScreenRoundName(number)}: ${topic}`;
}
/** §L33: past the Shortlist — a manager's Yes, or HR going ahead (the people who would be at Contact). */
function isPastShortlist(facts) {
    if (facts.stage !== 'screening')
        return false;
    if (facts.reviewStatus !== 'shortlisted')
        return false;
    return facts.managerResponse === 'yes' || facts.managerResponse === 'hr_proceeded';
}
/** In the Screening step now: past the Shortlist and not yet sent to Contact. */
function isInPhoneScreen(facts) {
    return facts.phoneScreen === 'in_screening' && isPastShortlist(facts);
}
/** The round a person is waiting in, or null when they have passed every round there is. */
function waitingRoundOf(passedRound, roundCount) {
    const next = passedRound + 1;
    return next <= roundCount ? next : null;
}
/**
 * Where a person shows on round `round`'s list, or null when they aren't on it. Screening N holds
 * only Screening N−1's passes (Q6b), so someone shows on every round up to the one they wait in,
 * passed on the earlier ones, and on the round they were screened out in.
 */
function placeInRound(person, round) {
    if (person.outRound !== null) {
        if (round < person.outRound)
            return 'passed';
        return round === person.outRound ? 'out' : null;
    }
    if (!person.isInStep)
        return null;
    if (round <= person.passedRound)
        return 'passed';
    return round === person.passedRound + 1 ? 'waiting' : null;
}
/** Why this person can't be given this result in this round now, in words, or null (§L25). */
function phoneScreenRefusal(person, round, roundCount, name) {
    if (person.outRound !== null)
        return `${name} was screened out already, so nothing changes. Undo the rejection first if it was a mistake.`;
    if (!person.isInStep)
        return `${name} isn't in Screening any more.`;
    if (roundCount === 0)
        return `Add ${phoneScreenRoundName(1)} first: say what the call checks.`;
    if (round < 1 || round > roundCount)
        return `There is no ${phoneScreenRoundName(round)}.`;
    if (round <= person.passedRound)
        return `${name} already passed ${phoneScreenRoundName(round)}.`;
    if (round > person.passedRound + 1)
        return `${name} hasn't passed ${phoneScreenRoundName(round - 1)}, so they aren't in ${phoneScreenRoundName(round)}.`;
    return null;
}
/** A pass is taken back only from the round it was given in, while nothing later has happened. */
function undoPassRefusal(person, round, name) {
    if (!person.isInStep || person.outRound !== null)
        return `${name} isn't in Screening any more, so the pass stays.`;
    if (person.passedRound !== round)
        return `${name} has moved on since, so the pass stays.`;
    return null;
}
/** The next round's number, or null when there are already as many as allowed. Rounds never skip a number. */
function nextRoundNumber(roundCount) {
    return roundCount < exports.PHONE_SCREEN_MAX_ROUNDS ? roundCount + 1 : null;
}
/** Only the last round goes, and only while nobody has a result in it. */
function roundRemovalRefusal(round, roundCount, people) {
    if (round !== roundCount)
        return `Only the last screening can be taken away. Take away ${phoneScreenRoundName(roundCount)} first.`;
    const decided = people.filter((person) => person.passedRound >= round || person.outRound === round).length;
    if (decided > 0)
        return `${decided} ${decided === 1 ? 'person has' : 'people have'} a result in ${phoneScreenRoundName(round)}, so it stays.`;
    return null;
}
/** Off only when nobody would be left waiting in a step that is no longer there (no one is ever lost). */
function switchOffRefusal(waiting) {
    if (waiting === 0)
        return null;
    return `${waiting} ${waiting === 1 ? 'person is' : 'people are'} still in Screening. Send them to Contact or screen them out first, then switch it off.`;
}
exports.switchPhoneScreenSchema = zod_1.z.object({ isOn: zod_1.z.boolean() });
const topicSchema = zod_1.z
    .string()
    .trim()
    .min(1, 'Say what this call checks.')
    .max(exports.PHONE_SCREEN_TOPIC_MAX, `Keep it under ${exports.PHONE_SCREEN_TOPIC_MAX} characters.`);
exports.addPhoneScreenRoundSchema = zod_1.z.object({ topic: topicSchema });
exports.renamePhoneScreenRoundSchema = zod_1.z.object({ topic: topicSchema, version: zod_1.z.number().int().positive() });
/** The address carries the version the screen saw (a DELETE has no body). */
exports.removePhoneScreenRoundSchema = zod_1.z.object({ version: zod_1.z.coerce.number().int().positive() });
exports.phoneScreenResultSchema = zod_1.z.object({
    applicationId: schemas_1.uuidSchema,
    round: zod_1.z.number().int().positive().max(exports.PHONE_SCREEN_MAX_ROUNDS),
    result: zod_1.z.enum(['pass', 'out']),
});
exports.undoPhoneScreenPassSchema = zod_1.z.object({ applicationId: schemas_1.uuidSchema, round: zod_1.z.number().int().positive().max(exports.PHONE_SCREEN_MAX_ROUNDS) });
/** At most a page of people at a time, as the shortlist's bulk moves. */
exports.PHONE_SCREEN_BULK_MAX = 200;
exports.phoneScreenPeopleSchema = zod_1.z.object({ applicationIds: zod_1.z.array(schemas_1.uuidSchema).min(1).max(exports.PHONE_SCREEN_BULK_MAX) });
//# sourceMappingURL=phone-screen.js.map